import { NgClass } from "@angular/common";
import { Component, ElementRef, HostListener, ViewChild } from "@angular/core";
import { PreviewItemFilterOptionComponent } from "@ponder-shared/tools/filter/preview-item-filter-option.component";
import { MagicCardFilterColor, MagicColorFilterOptions } from "../magic-card-filter-color";

type FilterOptionData = {
    selected: boolean;
    count: number;
}

@Component({
    selector: "magic-card-filter-option-color",
    imports: [NgClass],
    templateUrl: "./magic-card-filter-option-color.component.html"
})
export class MagicCardFilterOptionColorComponent extends PreviewItemFilterOptionComponent {
    public dropdownVisible: boolean = false;
    public colorFilter!: MagicCardFilterColor;

    @ViewChild("selectElement") private selectElementRef!: ElementRef<HTMLDivElement>;
    @ViewChild("allOption") private allOptionRef!: ElementRef<HTMLInputElement>;
    @ViewChild("whiteOption") private whiteOptionRef!: ElementRef<HTMLInputElement>;
    @ViewChild("blueOption") private blueOptionRef!: ElementRef<HTMLInputElement>;
    @ViewChild("blackOption") private blackOptionRef!: ElementRef<HTMLInputElement>;
    @ViewChild("redOption") private redOptionRef!: ElementRef<HTMLInputElement>;
    @ViewChild("greenOption") private greenOptionRef!: ElementRef<HTMLInputElement>;
    @ViewChild("multicolorOption") private multicolorOptionRef!: ElementRef<HTMLInputElement>;
    @ViewChild("colorlessOption") private colorlessOptionRef!: ElementRef<HTMLInputElement>;
    @ViewChild("orOption") private orOptionRef!: ElementRef<HTMLInputElement>;
    @ViewChild("andOption") private andOptionRef!: ElementRef<HTMLInputElement>;

    private allSelected: boolean = false;
    public white: FilterOptionData = { selected: true, count: 0 };
    public blue: FilterOptionData = { selected: true, count: 0 };
    public black: FilterOptionData = { selected: true, count: 0 };
    public red: FilterOptionData = { selected: true, count: 0 };
    public green: FilterOptionData = { selected: true, count: 0 };
    public multicolor: FilterOptionData = { selected: true, count: 0 };
    public colorless: FilterOptionData = { selected: true, count: 0 };

    private orSelected: boolean = true;
    private andSelected: boolean = false;

    // -------------------------------------------------------------------------------------
    // Overrides
    // -------------------------------------------------------------------------------------

    public override ngOnInit(): void {
        super.ngOnInit();

        this.colorFilter = this.filterOption as MagicCardFilterColor;
        this.white.count = this.colorFilter.options.white.count;
        this.blue.count = this.colorFilter.options.blue.count;
        this.black.count = this.colorFilter.options.black.count;
        this.red.count = this.colorFilter.options.red.count;
        this.green.count = this.colorFilter.options.green.count;
        this.multicolor.count = this.colorFilter.options.multicolor.count;
        this.colorless.count = this.colorFilter.options.colorless.count;

        this.white.selected = true;
        this.blue.selected = true;
        this.black.selected = true;
        this.red.selected = true;
        this.green.selected = true;
        this.multicolor.selected = true;
        this.colorless.selected = true;
        this.allSelected = true;
    }

    public override ngAfterViewInit(): void {
        super.ngAfterViewInit();
        this.updateColorSelection();
        this.updateLogicSelection();
        this.updateFilter();
    }

    public override loadStoredOptionValue(value: string): void {
        super.loadStoredOptionValue(value);
        
        var storedOptions = JSON.parse(value) as MagicColorFilterOptions;

        if (storedOptions) {
            this.white.selected = storedOptions.white.enabled;
            this.blue.selected = storedOptions.blue.enabled;
            this.black.selected = storedOptions.black.enabled;
            this.red.selected = storedOptions.red.enabled;
            this.green.selected = storedOptions.green.enabled;
            this.multicolor.selected = storedOptions.multicolor.enabled;
            this.colorless.selected = storedOptions.colorless.enabled;
            this.orSelected = storedOptions.or;
            this.andSelected = !storedOptions.or;

            this.updateColorSelection();
            this.updateLogicSelection();
        }
    }

    // -------------------------------------------------------------------------------------
    // Global Events
    // -------------------------------------------------------------------------------------

    private mouseDownOutside: boolean = false;

    @HostListener("document:mousedown", ["$event"])
    private onGlobalMouseDown(event: MouseEvent): void {
        if (this.dropdownVisible && !this.selfRef.nativeElement.contains(event.target as HTMLInputElement)) {
            this.mouseDownOutside = true;
        }
    }

    @HostListener("document:mouseup", ["$event"])
    private onGlobalMouseUp(event: MouseEvent): void {
        // Did the user click outside of our modal panel?
        if (this.dropdownVisible && this.mouseDownOutside && !this.selfRef.nativeElement.contains(event.target as HTMLInputElement))
        {
            this.dropdownVisible = false;
        }

        this.mouseDownOutside = false;
    }

    @HostListener("document:keydown.escape", ["$event"])
    private onEscapePressed(): void {
        this.dropdownVisible = false;
    }

    // -------------------------------------------------------------------------------------
    // Input Events
    // -------------------------------------------------------------------------------------

    public onSelectClicked(event: Event): void {
        event.stopPropagation();
        this.dropdownVisible = !this.dropdownVisible;
    }
    
    public onAllClicked(event: Event): void {
        event.stopPropagation();

        this.allSelected = !this.allSelected;
        this.white.selected = this.allSelected;
        this.blue.selected = this.allSelected;
        this.black.selected = this.allSelected;
        this.red.selected = this.allSelected;
        this.green.selected = this.allSelected;
        this.multicolor.selected = this.allSelected;
        this.colorless.selected = this.allSelected;
        
        this.updateColorSelection();
        this.updateFilter();
    }

    public onColorClicked(color: FilterOptionData, event: Event): void {
        event.stopPropagation();

        if (!color.count) {
            return;
        }

        color.selected = !color.selected;
        this.allSelected = false;

        this.updateColorSelection();
        this.updateFilter();
    }

    public onOrClicked(event: Event): void {
        event.stopPropagation();

        this.orSelected = !this.orSelected;
        this.andSelected = !this.orSelected;

        this.updateLogicSelection();
        this.updateFilter();
    }

    public onAndClicked(event: Event): void {
        event.stopPropagation();

        this.andSelected = !this.andSelected;
        this.orSelected = !this.andSelected;

        this.updateLogicSelection();
        this.updateFilter();
    }
    
    // -------------------------------------------------------------------------------------
    // Filter Update
    // -------------------------------------------------------------------------------------

    private updateColorSelection(): void {
        if (this.allSelected) {
            this.white.selected = true;
            this.blue.selected = true;
            this.black.selected = true;
            this.red.selected = true;
            this.green.selected = true;
            this.multicolor.selected = true;
            this.colorless.selected = true;
        } else {
            this.allSelected = 
                this.white.selected && 
                this.blue.selected && 
                this.black.selected && 
                this.red.selected && 
                this.green.selected && 
                this.colorless.selected;
        }

        this.allOptionRef.nativeElement.checked = this.allSelected;
        this.whiteOptionRef.nativeElement.checked =  this.white.selected && (this.white.count ? true : false);
        this.blueOptionRef.nativeElement.checked = this.blue.selected && (this.blue.count ? true : false);
        this.blackOptionRef.nativeElement.checked = this.black.selected && (this.black.count ? true : false);
        this.redOptionRef.nativeElement.checked = this.red.selected && (this.red.count ? true : false);
        this.greenOptionRef.nativeElement.checked =  this.green.selected && (this.green.count ? true : false);
        this.multicolorOptionRef.nativeElement.checked = this.multicolor.selected && (this.multicolor.count ? true : false);
        this.colorlessOptionRef.nativeElement.checked = this.colorless.selected && (this.colorless.count ? true : false);

        this.updateSelectText();
    }

    private updateSelectText()
    {
        if (!this.selectElementRef) {
            return;
        }

        var html = `<div class="grow">Color</div>`;

        if (this.whiteOptionRef.nativeElement.checked) { html += this.getColorDiv("W"); }
        if (this.blueOptionRef.nativeElement.checked) { html += this.getColorDiv("U"); }
        if (this.blackOptionRef.nativeElement.checked) { html += this.getColorDiv("B"); }
        if (this.redOptionRef.nativeElement.checked) { html += this.getColorDiv("R"); }
        if (this.greenOptionRef.nativeElement.checked) { html += this.getColorDiv("G"); }
        if (this.multicolorOptionRef.nativeElement.checked) { html += this.getColorDiv("M"); }
        if (this.colorlessOptionRef.nativeElement.checked) { html += this.getColorDiv("C"); }

        this.selectElementRef.nativeElement.innerHTML = html;
    }

    private getColorDiv(color: string): string {
        return `<div class="symbol-magic-xs" style="background-image: url('https://images.pondertcg.com/magic/symbol/${color}.svg');"></div>`;
    }

    public updateLogicSelection(): void {
        this.orOptionRef.nativeElement.checked = this.orSelected;
        this.andOptionRef.nativeElement.checked = this.andSelected;
    }

    private updateFilter(): void {
        this.colorFilter.updateColorSelection(this.white.selected, this.blue.selected, this.black.selected, this.red.selected, this.green.selected, this.multicolor.selected, this.colorless.selected, this.orSelected, this.page);
        this.events.ui.previewFilterChangeEvent.emit(this.itemFilters);
    }
}